import React, { useContext, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { useAlert } from "react-alert"
import { confirmAlert } from "react-confirm-alert"

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles"
import Paper from "@material-ui/core/Paper"
import Tooltip from "@material-ui/core/Tooltip"
import IconButton from "@material-ui/core/IconButton"
import Chip from "@material-ui/core/Chip"

// @material-ui/icons
import {
  Assignment,
  Dvr,
  DeleteForever,
  RemoveCircle,
  Check,
  Business,
  Add,
  Edit,
  ToggleOn,
  ToggleOff,
  CheckCircle,
  Warning,
  Info,
} from "@material-ui/icons"

// Private core components
import GridContainer from "components/Grid/GridContainer.js"
import GridItem from "components/Grid/GridItem.js"
import Button from "components/CustomButtons/Button.js"
import ReactTable from "components/ReactTable/ReactTable.js"

// Context & API
import authContext from "../../../context/AuthContext"
import {
  companyList,
  removeCompanyAndUser,
  suspendCompany,
} from "../../../api/companyAction"
import { compareArrayOfObject, parseBool } from "../../../utils/functions"
import config from "../../../config"

// Styling
import "react-confirm-alert/src/react-confirm-alert.css"
import { isNumber } from "underscore"
const placeHolder = require("assets/img/placeholder.jpg")

const localStyles = {
  mainContainer: {
    background: "linear-gradient(135deg, #667eea 0%, #0d142b 100%)",
    minHeight: "100vh",
    padding: "40px 20px",
    borderRadius: "12px",
  },
  contentWrapper: {
    maxWidth: "1400px",
    margin: "0 auto",
  },
  headerCard: {
    background: "white",
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "24px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#2d3748",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  pageSubtitle: {
    fontSize: "16px",
    color: "#718096",
    fontWeight: "400",
    marginBottom: "24px",
  },
  statsRow: {
    display: "flex",
    gap: "16px",
    marginTop: "24px",
    flexWrap: "wrap",
  },
  statCard: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "20px",
    borderRadius: "12px",
    flex: "1",
    minWidth: "200px",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
  },
  statNumber: {
    fontSize: "32px",
    fontWeight: "700",
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "14px",
    opacity: "0.9",
  },
  tableCard: {
    background: "white",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "2px solid #e2e8f0",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  sectionIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
  },
  titleText: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#2d3748",
    margin: 0,
  },
  addButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "12px 24px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
    },
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  actionButton: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    border: "none",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  editButton: {
    background: "#3b82f6",
    color: "white",
    "&:hover": {
      background: "#2563eb",
    },
  },
  toggleButton: {
    background: "#10b981",
    color: "white",
    "&:hover": {
      background: "#059669",
    },
  },
  toggleButtonInactive: {
    background: "#f59e0b",
    color: "white",
    "&:hover": {
      background: "#d97706",
    },
  },
  deleteButton: {
    background: "#ef4444",
    color: "white",
    "&:hover": {
      background: "#dc2626",
    },
  },
  statusBadge: {
    padding: "4px 12px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "600",
  },
  statusComplete: {
    background: "#d1fae5",
    color: "#065f46",
  },
  statusIncomplete: {
    background: "#fef3c7",
    color: "#92400e",
  },
  companyImage: {
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    objectFit: "cover",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  infoBox: {
    background: "#eff6ff",
    border: "2px solid #bfdbfe",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "24px",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  infoIcon: {
    color: "#3b82f6",
    marginTop: "2px",
  },
  infoText: {
    fontSize: "14px",
    color: "#1e40af",
    lineHeight: "1.6",
    margin: 0,
  },
  docLink: {
    color: "#667eea",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "600",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}

const useStyles = makeStyles(localStyles)

const label = {
  pageTitle: "Gestion des Établissements",
  pageSubtitle:
    "Gérez tous vos établissements, modifiez leurs informations et surveillez leur statut",
  totalCompanies: "Total établissements",
  activeCompanies: "Actifs",
  completeCompanies: "Complets",
  tableTitle: "Liste des établissements",
  addCompany: "Nouvel établissement",
  messageOK: "Bravo, modification effectuée !",
  messageKO: "Aie, veuillez réessayer ou contacter l'administrateur.",
  confirmDelete: "Êtes-vous sûr de vouloir supprimer",
  confirmDeleteQuestion: "?",
  yes: "Oui",
  no: "Non",
  edit: "Modifier",
  toggleStatus: "Activer/Désactiver",
  delete: "Supprimer",
  complete: "Complet",
  incomplete: "Incomplet",
  infoMessage:
    "Un établissement est considéré comme 'complet' s'il possède un email, des coordonnées GPS et des horaires d'ouverture.",
  documentation: "Documentation",
}

const BASE_URL = config.BASE_URL

export function CompanyList() {
  const { mpId } = useContext(authContext)
  const history = useHistory()
  const alert = useAlert()
  const classes = useStyles()

  const [data, setData] = useState(undefined)
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    complete: 0,
  })

  useEffect(() => {
    if (mpId) {
      companyList(mpId)
        .then((response) => {
          if (response.status === 200 && response.data.status === 1) {
            let st = response.data.data
            if (st.length > 0) {
              st.sort(compareArrayOfObject)

              // Calculer les statistiques
              const totalCount = st.length
              const activeCount = st.filter((v) => parseBool(v.is_active)).length
              const completeCount = st.filter((v) => {
                return (
                  v.email &&
                  isNumber(v.latitude) &&
                  isNumber(v.longitude) &&
                  v.working_hours
                )
              }).length

              setStats({
                total: totalCount,
                active: activeCount,
                complete: completeCount,
              })
              console.log("Company list fetched :", st)

              setData(
                st.map((v) => {
                  let isComplete = false

                  if (
                    v.email &&
                    isNumber(v.latitude) &&
                    isNumber(v.longitude) &&
                    v.working_hours
                  ) {
                    isComplete = true
                  }

                  return {
                    id: v.id,
                    name: v.name,
                    city: v.city,
                    latitude: v.latitude,
                    longitude: v.longitude,
                    email: v.email,
                    lastname: v.lastname,
                    is_active: parseBool(v.is_active),
                    working_hours: v.working_hours,
                    is_complete: isComplete,
                    actions: actionButton(st, v.id, v.is_active, isComplete),
                    updated_at: v.updated_at ?? Date.now(),
                    is_firebase: v.is_firebase ?? 0,
                    cover_url: v.cover_url ?? null,
                  }
                })
              )
            }
          }
        })
        .catch((e) => console.log(e))
    }
  }, [mpId])

  const handleDelete = (_newData, _id, _companyName) => {
    confirmAlert({
      title: label.confirmDelete,
      message: `${label.confirmDelete} ${_companyName} ${label.confirmDeleteQuestion}`,
      buttons: [
        {
          label: label.yes,
          onClick: () => {
            removeCompanyAndUser(_id)
              .then((response) => {
                console.log("delete company response :", response)
                console.log("new data after delete :", _newData)
                setData([..._newData])
                alert.success(label.messageOK)

                // Mettre à jour les stats
                setStats((prev) => ({
                  ...prev,
                  total: prev.total - 1,
                }))
              })
              .catch((e) => {
                console.log(e)
                alert.error(label.messageKO)
              })
          },
        },
        {
          label: label.no,
          onClick: () => {},
        },
      ],
    })
  }

  const actionButton = (_company, _id, _isActive = true, isComplete = true) => {
    return (
      <div className={classes.actionButtons}>
        <Tooltip title={label.edit}>
          <button
            className={`${classes.actionButton} ${classes.editButton}`}
            onClick={() => {
              let obj = _company.find((o) => o.id === _id)
              if (obj) {
                const path = "/admin/company-manager"
                history.push({
                  pathname: path,
                  state: { cid: obj.id },
                })
              }
            }}
          >
            <Edit style={{ fontSize: "18px" }} />
          </button>
        </Tooltip>

        <Tooltip title={label.toggleStatus}>
          <button
            className={`${classes.actionButton} ${
              parseBool(_isActive)
                ? classes.toggleButton
                : classes.toggleButtonInactive
            }`}
            onClick={() => {
              let obj = _company.find((o) => o.id === _id)
              if (obj) {
                suspendCompany(_id)
                  .then((response) => {
                    alert.success(label.messageOK)
                    const updatedData = _company.map((x) =>
                      x.id === _id
                        ? {
                            ...x,
                            is_active:
                              x.is_active == "0" || x.is_active === false ? 1 : 0,
                          }
                        : x
                    )
                    updatedData.map((obj) => {
                      obj.actions = actionButton(
                        updatedData,
                        obj.id,
                        obj.is_active,
                        obj.is_complete
                      )
                    })
                    setData([...updatedData])
                  })
                  .catch((e) => {
                    console.log(e)
                    alert.error(label.messageKO)
                  })
              }
            }}
          >
            {parseBool(_isActive) ? (
              <ToggleOn style={{ fontSize: "18px" }} />
            ) : (
              <ToggleOff style={{ fontSize: "18px" }} />
            )}
          </button>
        </Tooltip>

        <Tooltip title={label.delete}>
          <button
            className={`${classes.actionButton} ${classes.deleteButton}`}
            onClick={() => {
              // console.log("company after delete :", _company)
              let newData = _company
              let companyName = ""
              newData.find((o, i) => {
                if (o.id === _id) {
                  newData.splice(i, 1)
                  companyName = o.name
                  return true
                }
                return false
              })

              newData.map((obj) => {
                obj.actions = actionButton(
                  newData,
                  obj.id,
                  obj.is_active,
                  obj.is_complete
                )
              })
              handleDelete(newData, _id, companyName)
            }}
          >
            <DeleteForever style={{ fontSize: "18px" }} />
          </button>
        </Tooltip>

        <Tooltip title={isComplete ? label.complete : label.incomplete}>
          <div
            className={`${classes.statusBadge} ${
              isComplete ? classes.statusComplete : classes.statusIncomplete
            }`}
          >
            {isComplete ? (
              <CheckCircle style={{ fontSize: "16px" }} />
            ) : (
              <Warning style={{ fontSize: "16px" }} />
            )}
          </div>
        </Tooltip>
      </div>
    )
  }

  const handleAddCompany = () => {
    const path = "/admin/company-manager"
    history.push({ pathname: path })
  }

  return (
    <div className={classes.mainContainer}>
      <div className={classes.contentWrapper}>
        {/* Header */}
        <div className={classes.headerCard}>
          <div className={classes.pageTitle}>
            <Business style={{ fontSize: "40px", color: "#667eea" }} />
            {label.pageTitle}
          </div>
          <p className={classes.pageSubtitle}>{label.pageSubtitle}</p>

          {/* Statistiques */}
          <div className={classes.statsRow}>
            <div className={classes.statCard}>
              <div className={classes.statNumber}>{stats.total}</div>
              <div className={classes.statLabel}>{label.totalCompanies}</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statNumber}>{stats.active}</div>
              <div className={classes.statLabel}>{label.activeCompanies}</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statNumber}>{stats.complete}</div>
              <div className={classes.statLabel}>{label.completeCompanies}</div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className={classes.infoBox}>
          <Info className={classes.infoIcon} />
          <div>
            <p className={classes.infoText}>{label.infoMessage}</p>
            <a
              href="/faq#menu-3"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.docLink}
            >
              {label.documentation} →
            </a>
          </div>
        </div>

        {/* Table */}
        <Paper className={classes.tableCard}>
          <div className={classes.sectionHeader}>
            <div className={classes.sectionTitle}>
              <div className={classes.sectionIcon}>
                <Assignment />
              </div>
              <h3 className={classes.titleText}>{label.tableTitle}</h3>
            </div>
            <button className={classes.addButton} onClick={handleAddCompany}>
              <Add style={{ fontSize: "20px" }} />
              {label.addCompany}
            </button>
          </div>

          {data ? (
            <ReactTable
              name="company"
              columns={[
                {
                  Header: "Visuel",
                  Cell: (tableProps) => (
                    <div>
                      <img
                        className={classes.companyImage}
                        src={
                          tableProps.row.original.is_firebase === 1
                            ? tableProps.row.original.cover_url
                            : BASE_URL +
                              "img/company/cover-" +
                              tableProps.row.original.id +
                              ".jpg?" +
                              tableProps.row.original.updated_at
                        }
                      />
                    </div>
                  ),
                },
                {
                  Header: "Société",
                  accessor: "name",
                },
                {
                  Header: "Responsable",
                  accessor: "lastname",
                },
                {
                  Header: "Ville",
                  accessor: "city",
                },
                {
                  Header: "Email",
                  accessor: "email",
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                },
              ]}
              data={data}
            />
          ) : null}
        </Paper>
      </div>
    </div>
  )
}
